"use client";

import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Close, EmojiEvents, Group, Smartphone } from "@mui/icons-material";

interface AwardYearInReviewDialogProps {
  open: boolean;
  onClose: () => void;
  ctaData: {
    award: any;
    path: string;
  } | null;
  onConfirm: (path: string) => void;
  getAwardGradient: (type: string) => string;
}

export const AwardYearInReviewDialog: React.FC<
  AwardYearInReviewDialogProps
> = ({ open, onClose, ctaData, onConfirm, getAwardGradient }) => {
  if (!ctaData) return null;

  const renderCtaIcon = () => {
    const cta = ctaData.award.cta;
    const commonSx = {
      fontSize: 64,
      filter: "drop-shadow(0 0 15px rgba(255, 255, 255, 0.2))",
    };

    if (cta === "Learn More about Team Account") {
      return <Group sx={{ ...commonSx, color: "#06b6d4" }} />;
    }
    if (cta === "Upgrade to CBA Package") {
      return <Smartphone sx={{ ...commonSx, color: "#ec4899" }} />;
    }
    return <EmojiEvents sx={{ ...commonSx, color: "#f59e0b" }} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      disablePortal={false}
      keepMounted={false}
      sx={{
        zIndex: 99999,
        "& .MuiDialog-container": {
          alignItems: "center",
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: "#0a0a0a",
          color: "white",
          borderRadius: 4,
          mx: { xs: 2, sm: "auto" },
          position: "relative",
          overflow: "hidden",
          backgroundImage: "none",
          boxShadow: "0 20px 50px rgba(0,0,0,0.8)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: getAwardGradient(ctaData.award.type),
            opacity: 0.15,
            zIndex: 0,
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "rgba(255,255,255,0.5)",
          zIndex: 10,
          "&:hover": { color: "white" },
        }}
      >
        <Close />
      </IconButton>

      <DialogContent
        sx={{
          p: { xs: 3, sm: 5 },
          position: "relative",
          zIndex: 1,
          textAlign: "center",
        }}
      >
        <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
          {renderCtaIcon()}
        </Box>

        <Typography
          variant="h5"
          sx={{ fontWeight: 800, mb: 3, lineHeight: 1.2 }}
        >
          {ctaData.award.cta}
        </Typography>

        <Box sx={{ mb: 5, textAlign: "center" }}>
          {ctaData.award.cta === "Learn More about Team Account" ? (
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  lineHeight: 1.6,
                  mb: 2.5,
                }}
              >
                Scale your business without burnout. The{" "}
                <strong>Team Account</strong> lets you delegate tasks while
                maintaining total authority over billing and data.
              </Typography>
              <Box
                sx={{
                  bgcolor: "rgba(6, 182, 212, 0.1)",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(6, 182, 212, 0.2)",
                }}
              >
                <Typography sx={{ color: "#06b6d4", fontWeight: 700, mb: 0.5 }}>
                  Better for your clients
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}
                >
                  Faster replies, more attention, and 5-star service. Focus on
                  the big picture while your team handles the rest.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography
                variant="h6"
                sx={{ color: "#ec4899", fontWeight: 800, mb: 2.5 }}
              >
                Your Brand. Your App.
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  lineHeight: 1.6,
                  mb: 2.5,
                }}
              >
                Upgrade to a <strong>Custom Branded App</strong>. We remove all
                Stridist mentions so clients see only your logo, colors, and
                branding from login to logout.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)", fontStyle: "italic" }}
              >
                Clients download your app directly from the App Store. To them,
                it's your own proprietary software.
              </Typography>
            </Box>
          )}
        </Box>

        {(ctaData.award.type !== "mostChatty") && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              onClick={() => onConfirm(ctaData.path)}
              sx={{
                background: getAwardGradient(ctaData.award.type),
                color: "white",
                fontWeight: 800,
                px: { xs: 4, sm: 6 },
                py: 1.8,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1rem",
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.4)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                  filter: "brightness(1.1)",
                },
              }}
            >
              {ctaData.award.cta === "Upgrade to CBA Package"
                ? "Get My Own Branded App"
                : "Explore Team Account"}
            </Button>
          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
};
